// AIChadGreeting v2 — unified My Desk banner.
// Two-column layout: chat box (left) + meta panel (right: clock, weather, stats,
// customize/health). Always open (no expand/collapse). Footer row holds tallies,
// centered quote (xl+), and the date.
//
// Strict scope: My Desk only. Does NOT replace the bottom-left AI Chad floater
// (AiChad.js) and does NOT share state with DayStartGuide.js.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Settings, ChevronDown, ChevronUp, Route } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import aiChadAvatar from '@/assets/ai-chad-avatar.png';
import { getCurrentUserId } from '../../utils/currentUser';
import JourneySteps from '@/components/project/JourneySteps';
import { getPhaseItems, isStepCompleted, isStepNotNeeded, computeJourneyProgress } from '@/components/project/journey-constants';
import { ActionCard } from './ActionCard';
import { renderChadMarkdown } from './chadMarkdown';
import '@/styles/ai-chad.css';
import {
  useWeather,
  useSystemHealth,
  useQuoteOfDay,
  useLiveTime,
} from '@/hooks/useDashboardWidgets';

const API = process.env.REACT_APP_BACKEND_URL;

// MD-2 — the window the desk's Since You Left card reads, so Chad speaks about
// the same changes. No stamp yet (a first visit) means the last twelve hours.
const lastSeenStamp = () => {
  try {
    return window.localStorage.getItem('keyMadeLastSeen')
      || new Date(Date.now() - 12 * 3600 * 1000).toISOString();
  } catch {
    return new Date(Date.now() - 12 * 3600 * 1000).toISOString();
  }
};

// getGreeting — copied verbatim from DayStartGuide.js lines 43-49 (byte-identical
// to v1). Do not import from DayStartGuide so this component stays decoupled.
function getGreeting(hour) {
  if (hour < 6) return { text: 'Working late?', emoji: '🌙', subtext: "Don't forget to rest!" };
  if (hour < 12) return { text: 'Good morning', emoji: '☀️', subtext: "Let's make today count!" };
  if (hour < 17) return { text: 'Good afternoon', emoji: '🌤️', subtext: 'Keep up the momentum!' };
  if (hour < 21) return { text: 'Good evening', emoji: '🌅', subtext: 'Wrapping up the day?' };
  return { text: 'Good night', emoji: '🌙', subtext: 'Rest well!' };
}

const PLACEHOLDER_GREETING = "Hey Chad — what's on deck?";

function toneClass(tone) {
  switch (tone) {
    case 'danger': return 'text-red-500 font-semibold';
    case 'success': return 'text-green-500 font-semibold';
    case 'warning': return 'text-amber-500 font-semibold';
    default: return 'text-foreground font-semibold';
  }
}

function Tally({ tally }) {
  return (
    <span className="text-[11px] text-muted-foreground">
      <strong className={toneClass(tally.tone)}>{tally.value}</strong>{' '}
      {tally.label}
    </span>
  );
}

function StatCell({ value, label, valueClass = 'text-foreground', labelClass = 'text-muted-foreground' }) {
  return (
    <div className="text-center">
      <p className={`text-lg font-medium leading-none m-0 ${valueClass}`}>{value}</p>
      <p className={`text-[10px] mt-0.5 m-0 ${labelClass}`}>{label}</p>
    </div>
  );
}

export default function AIChadGreeting({
  userName,
  screen = 'mydesk',
  projectId,
  todayTasks,
  calendarEvents,
  darkMode = false,
  tallies = [],
  dateLabel,
  userId = getCurrentUserId(),
  onCustomizeClick,
  // Combined guide panel (project pages): compact JourneySteps strip rendered
  // inside this block, below the chat. Omit journeyProject to hide (My Desk).
  journeyProject = null,
  journeyPhases = null,
  onJourneyUpdate,
  journeyTeamMembers = [],
  // DESIGN PASS 17 — AI Chad's conversation moved to its own desk card. The
  // masthead keeps the meta panel and the quote and passes hideChat; the card
  // passes chatOnly and renders the conversation on its own dark ground.
  hideChat = false,
  hideQuote = false,
  chatOnly = false,
  // PS-1f — on a stage desk the standing read replaces the header and opening line.
  compact = false,
}) {
  // Shared hooks (replace prop versions from v2.original spec)
  const { temp, conditions, emoji, high, low } = useWeather();
  const { status: systemHealth } = useSystemHealth(userId);
  const { text: quoteText, author: quoteAuthor } = useQuoteOfDay();
  const { now } = useLiveTime();

  // hour preserved for getGreeting() use (function retained per spec even though
  // its return value is no longer rendered).
  const [dynamicGreeting, setDynamicGreeting] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  // Stage 2: Activity Log proposal cards rendered at the bottom of the transcript.
  // Cleared as the user approves or dismisses each.
  const [logProposals, setLogProposals] = useState([]);
  // Hydration flag — true once we've checked the server for prior history.
  // Used to suppress the static greeting placeholder while we're still loading.
  const [hydrated, setHydrated] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  const transcriptRef = useRef(null);
  const inputRef = useRef(null);
  const refocusRef = useRef(false);

  // Journey strip collapse — persisted per user; default expanded on desktop,
  // collapsed on small/mobile widths.
  const showJourneyStrip = Boolean(journeyProject);
  // Divider-row glance info (replaces the retired right-column MiniJourneyPreview):
  // current phase (first phase with incomplete items) + overall journey progress,
  // using the shared unified math from journey-constants.js.
  const effectivePhases = journeyPhases || journeyProject?.phases || [];
  const { currentPhaseName, journeyPct } = React.useMemo(() => {
    if (!effectivePhases.length) return { currentPhaseName: null, journeyPct: null };
    const current = effectivePhases.find((phase) =>
      getPhaseItems(phase).some((s) => !isStepCompleted(s) && !isStepNotNeeded(s))
    ) || effectivePhases[effectivePhases.length - 1];
    return {
      currentPhaseName: current?.name || null,
      journeyPct: computeJourneyProgress(effectivePhases).progress,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyPhases, journeyProject]);
  const stripStorageKey = `chadJourneyStripCollapsed:${userId || 'anon'}`;
  const [stripCollapsed, setStripCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(stripStorageKey);
      if (stored !== null) return stored === '1';
    } catch (e) { /* private mode */ }
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  const toggleStrip = () => {
    setStripCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(stripStorageKey, next ? '1' : '0'); } catch (e) { /* noop */ }
      return next;
    });
  };

  // Cold linking (Chad text → strip). renderChadMarkdown turns [[step:...]]
  // tokens into quiet inline links wired to these handlers; JourneySteps
  // listens for the events and highlights/opens the matching card. All
  // USER-TRIGGERED — no auto-motion while Chad talks. Without a journey
  // strip (My Desk) no opts are passed and tokens degrade to plain text.
  // Stage C: My Desk has no project strip, but the onboarding strips below
  // the Chad block listen for the same step events. When they're active,
  // greeting/chat step tokens become live links dispatched globally.
  const [onboardingStripsActive, setOnboardingStripsActive] = useState(
    () => (typeof window !== 'undefined' && window.__kmOnboardingStripsActive) || false
  );
  useEffect(() => {
    const onStrips = (e) => setOnboardingStripsActive(Boolean(e.detail?.active));
    window.addEventListener('keymade:onboarding-strips', onStrips);
    return () => window.removeEventListener('keymade:onboarding-strips', onStrips);
  }, []);

  // SC-3 — a question chip elsewhere on the page raises 'open-ai-chad' with its
  // prefill; the card Chad lives in puts it in the box and waits for Send.
  useEffect(() => {
    const take = (q) => {
      if (!q) return;
      setInput(q);
      inputRef.current?.scrollIntoView({ block: 'center' });
      setTimeout(() => inputRef.current?.focus(), 150);
    };
    const onAsk = (e) => take(e?.detail?.prefill);
    window.addEventListener('open-ai-chad', onAsk);
    // The chip may have fired before this card existed.
    if (typeof window !== 'undefined' && window.__kmChadPrefill) {
      take(window.__kmChadPrefill);
      window.__kmChadPrefill = null;
    }
    return () => window.removeEventListener('open-ai-chad', onAsk);
  }, []);

  const stepLinkOpts = showJourneyStrip ? {
    onStepHover: (stepId, hovering) => {
      if (stripCollapsed) return; // nothing to highlight; click will expand
      window.dispatchEvent(new CustomEvent('keymade:journey-step-hover', { detail: { stepId, hovering } }));
    },
    onStepClick: (stepId) => {
      const fire = () => window.dispatchEvent(
        new CustomEvent('keymade:journey-step-open', { detail: { stepId } })
      );
      if (stripCollapsed) {
        // Expand first, then open once the strip has mounted.
        setStripCollapsed(false);
        try { localStorage.setItem(stripStorageKey, '0'); } catch (e) { /* noop */ }
        setTimeout(fire, 150);
        return;
      }
      fire();
    },
  } : (onboardingStripsActive ? {
    // Onboarding strips own collapse/expand themselves (they re-fire the
    // open event after expanding) — plain global dispatch is enough here.
    onStepHover: (stepId, hovering) => {
      window.dispatchEvent(new CustomEvent('keymade:journey-step-hover', { detail: { stepId, hovering } }));
    },
    onStepClick: (stepId) => {
      window.dispatchEvent(new CustomEvent('keymade:journey-step-open', { detail: { stepId } }));
    },
  } : undefined);

  // On mount: load conversation history first. Only fetch the dynamic greeting
  // if the thread is empty (first-time user). If userId is missing we degrade
  // gracefully to the v3 behavior (no persistence, just greeting fetch).
  useEffect(() => {
    let cancelled = false;
    let greetingTimer = null;
    (async () => {
      // 1) Load prior conversation
      let priorMessages = [];
      if (userId) {
        try {
          const res = await axios.get(`${API}/api/ai-chad/conversation`, {
            params: { user_id: userId, limit: 50 },
          });
          priorMessages = res?.data?.messages || [];
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('AIChadGreeting: history load failed', err);
        }
      }
      if (cancelled) return;

      if (priorMessages.length > 0) {
        // Map server shape (timestamp, project_id) onto local shape (created_at).
        setMessages(priorMessages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          created_at: m.timestamp,
          screen: m.screen,
          proposed_actions: m.proposed_actions || [],
        })));
        setHydrated(true);
        return;
      }

      setHydrated(true);

      // 2) Empty thread — fetch dynamic greeting and show as the first bubble.
      // SL-1b — delay 1.5 s so the page's own data calls get the loop first,
      // and skip entirely when the tab is hidden.
      greetingTimer = setTimeout(() => {
        if (cancelled) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        (async () => {
          try {
            const params = { screen };
            if (projectId) params.project_id = projectId;
            if (screen === 'mydesk') params.since = lastSeenStamp();   // MD-2
            const res = await axios.get(`${API}/api/ai-chad/greeting`, { params });
            if (!cancelled && res?.data?.greeting) {
              setDynamicGreeting(res.data.greeting);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('AIChadGreeting: greeting fetch failed', err);
          }
        })();
      }, 1500);
    })();
    return () => { cancelled = true; if (greetingTimer) clearTimeout(greetingTimer); };
  }, [screen, projectId, userId]);

  // Real-time sync via WebSocket. Stage 1.5.
  // Receives `messages_appended` broadcasts from any tab/device for this user
  // and merges them into local state (dedupe by message id, since our own
  // append echoes back too). Reconnects with exponential backoff on drop.
  useEffect(() => {
    if (!userId) return undefined;

    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');
    // Round 4 Phase 1c: JWT required as query param. user_id query value is
    // ignored server-side — identity is bound from the JWT claim.
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    if (!token) return undefined;
    const wsUrl = `${base}/api/ai-chad/conversation/stream?token=${encodeURIComponent(token)}`;

    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let intentionallyClosed = false;
    let backoffMs = 1000;
    // Bugfix (Rollout 3 of AI Chad rollout — Feb 2026):
    // `hasEverConnected` was referenced in ws.onopen below without being declared,
    // throwing ReferenceError under strict mode the moment the socket connected.
    // Triggered intermittently across pages depending on connection race timing.
    let hasEverConnected = false;
    // Quiet retry logger — referenced by ws.onclose + the construct-fail catch
    // below. Debug-level only so we don't spam the console on every navigation
    // (the close handler fires every time the user changes routes or the
    // proxy times out an idle socket).
    const logRetry = (msg, err) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[AIChadGreeting]', msg, err || '');
      }
    };

    const mergeIncoming = (incoming) => {
      setMessages((prev) => {
        const have = new Set(prev.map((m) => m.id));
        const adds = (incoming || [])
          .filter((m) => m && m.id && !have.has(m.id))
          .map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.timestamp,
            screen: m.screen,
            proposed_actions: m.proposed_actions || [],
          }));
        if (adds.length === 0) return prev;
        return [...prev, ...adds];
      });
    };

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
      } catch (err) {
        logRetry('construct failed', err);
        return;
      }

      ws.onopen = () => {
        hasEverConnected = true;
        backoffMs = 1000;
        // Keepalive — survives proxies that kill idle WS.
        pingTimer = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'ping' })); } catch (e) { /* noop */ }
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data && data.type === 'messages_appended' && Array.isArray(data.messages)) {
            mergeIncoming(data.messages);
          }
          // 'connected' and 'pong' are informational; ignore
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('AIChadGreeting WS parse error', err);
        }
      };

      ws.onclose = () => {
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (intentionallyClosed) return;
        logRetry(`connection closed, reconnecting in ${backoffMs}ms`);
        reconnectTimer = setTimeout(connect, backoffMs);
        backoffMs = Math.min(backoffMs * 2, 30000);
      };

      ws.onerror = () => {
        // close handler will trigger the (quiet) retry log
      };
    };

    connect();

    return () => {
      intentionallyClosed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (ws) {
        try { ws.close(); } catch (e) { /* noop */ }
      }
    };
  }, [userId]);

  // Auto-scroll transcript on new messages / thinking state.
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages.length, isThinking, dynamicGreeting]);

  const sendMessage = useCallback(async (rawText) => {
    const text = (rawText || '').trim();
    if (!text || isThinking) return;
    const userTimestamp = new Date().toISOString();
    const userMsg = {
      id: uuidv4(),
      role: 'user',
      content: text,
      created_at: userTimestamp,
      screen,
    };
    const history = messages.map((m) => ({ role: m.role, content: m.content, screen: m.screen || screen }));
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsThinking(true);
    try {
      const res = await axios.post(`${API}/api/ai-chad/chat`, {
        message: text,
        current_screen: screen,
        project_id: projectId || null,
        conversation_history: history,
        // Stage 2: pass message id + user_id so backend can apply TAG: blocks
        // to the persisted user message in-place.
        user_message_id: userMsg.id,
        user_id: userId || null,
        since: screen === 'mydesk' ? lastSeenStamp() : null,   // MD-2
      });
      const reply = res?.data?.response || '…';
      const tagProjectIds = Array.isArray(res?.data?.tag_project_ids) ? res.data.tag_project_ids : [];
      const incomingProposals = Array.isArray(res?.data?.log_proposals) ? res.data.log_proposals : [];
      const writeProposals = Array.isArray(res?.data?.proposed_actions) ? res.data.proposed_actions : [];
      const assistantTimestamp = new Date().toISOString();
      const assistantMsg = {
        id: uuidv4(),
        role: 'assistant',
        content: reply,
        created_at: assistantTimestamp,
        screen,
        proposed_actions: writeProposals,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      if (incomingProposals.length > 0) {
        setLogProposals((prev) => [...prev, ...incomingProposals]);
      }

      // Fire-and-forget persistence. UI is already updated; we don't block on it.
      // Stage 2: messages now carry `project_ids` (array). If the chat endpoint
      // returned tag_project_ids, both messages get tagged in the persisted form.
      if (userId) {
        const tagsForBoth = (tagProjectIds.length > 0)
          ? tagProjectIds
          : (projectId ? [projectId] : []);
        axios.post(`${API}/api/ai-chad/conversation/append`, {
          user_id: userId,
          messages: [
            {
              id: userMsg.id,
              role: 'user',
              content: text,
              screen,
              project_ids: tagsForBoth,
              timestamp: userTimestamp,
            },
            {
              id: assistantMsg.id,
              role: 'assistant',
              content: reply,
              screen,
              project_ids: tagsForBoth,
              timestamp: assistantTimestamp,
              proposed_actions: writeProposals,
            },
          ],
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('AIChadGreeting: append persistence failed', err);
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AIChadGreeting: chat error', err);
      setMessages((prev) => [...prev, {
        id: uuidv4(), role: 'assistant',
        content: 'Sorry — I had trouble with that. Try again?',
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setIsThinking(false);
    }
  }, [messages, isThinking, screen, projectId, userId]);

  const noteActionResult = useCallback((taken) => {
    setMessages((prev) => [...prev, {
      id: uuidv4(),
      role: 'user',
      content: `actions_taken: ${JSON.stringify([taken])}`,
      created_at: new Date().toISOString(),
      screen,
      hidden: true,
    }]);
  }, [screen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // AC-1 — the field is disabled while he thinks, which drops focus. Asked
    // from here, it is given back the moment he answers, so a second question
    // needs no click.
    refocusRef.current = true;
    sendMessage(input);
  };

  useEffect(() => {
    if (!isThinking && refocusRef.current) {
      refocusRef.current = false;
      inputRef.current?.focus();
    }
  }, [isThinking]);

  // Stage 2: Activity Log proposal handlers.
  const approveProposal = useCallback(async (proposal) => {
    setLogProposals((prev) => prev.filter((p) => p.id !== proposal.id));
    try {
      await axios.post(`${API}/api/ai-chad/conversation/log-approve`, {
        user_id: userId || getCurrentUserId(),
        proposal: {
          title: proposal.title,
          description: proposal.description || '',
          activity_type: proposal.activity_type || 'chad_note',
          project_id: proposal.project_id || null,
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AIChadGreeting: log-approve failed', err);
      // Restore the card so the user can retry.
      setLogProposals((prev) => [proposal, ...prev]);
    }
  }, [userId]);

  const dismissProposal = useCallback(async (proposal) => {
    setLogProposals((prev) => prev.filter((p) => p.id !== proposal.id));
    try {
      await axios.post(`${API}/api/ai-chad/conversation/log-dismiss`, {
        user_id: userId || getCurrentUserId(),
        proposal_id: proposal.id,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('AIChadGreeting: log-dismiss failed (non-critical)', err);
    }
  }, [userId]);

  // ---- Derived display values ----
  // Stats
  const overdueCount = todayTasks?.overdue?.length || 0;
  const todayCount = todayTasks?.today?.length || 0;
  const completedCount = todayTasks?.completed?.length || 0;
  const totalTasks = overdueCount + todayCount;
  const eventsToday = calendarEvents?.length || 0;

  // Clock breakdown for hero layout: "7:09" and "PM · MONDAY · APR 27"
  const hourMinute = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s?(AM|PM)$/i, '');
  const ampm = now.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }).trim().slice(-2);
  const weekdayLong = now.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  const monthDay = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();

  // Date label retained for future use even though footer no longer renders it.
  // (Spec retains tallies prop architecture; date is currently shown only under the clock.)

  // System health indicator label + dot color
  const healthMeta = systemHealth === 'warning'
    ? { label: 'Warning', dot: 'bg-amber-500' }
    : systemHealth === 'down'
      ? { label: 'Down', dot: 'bg-red-500' }
      : { label: 'Healthy', dot: 'bg-green-500' };

  // DESIGN PASS 19 — on the masthead the greeting sits DIRECTLY on the band:
  // no card, no panel, no fill. The band is one surface.
  const bandOnly = hideChat;
  // Card container colors — same rounded card whether light/dark
  const cardClass = bandOnly ? 'bg-transparent border-0' : darkMode
    ? 'bg-[#0D0D0D] border border-[#1A1A1A]'
    : 'bg-white border border-gray-200';
  const dividerClass = bandOnly ? 'border-[color:var(--km-rule-soft)]' : darkMode ? 'border-[#1A1A1A]' : 'border-gray-200';
  // DESIGN PASS 20 — on the band, every ink is a KEY-MADE token. shadcn's
  // text-muted-foreground passed on the white panel and failed the moment the
  // panel went; it is not ours and it does not know about the band.
  const bandInk = bandOnly
    ? 'text-[color:var(--km-ink)]'
    : (darkMode ? 'text-[#E5E2DD]' : 'text-foreground');
  const bandInk2 = bandOnly ? 'text-[color:var(--km-ink-2)]' : 'text-muted-foreground';
  const bandLate = bandOnly ? 'text-[color:var(--km-late)]' : 'text-red-500';
  const innerChatBoxClass = darkMode ? 'bg-[#141414]' : 'bg-gray-50';
  const inputClass = darkMode
    ? 'border border-[#262626] bg-[#1A1A1A] text-[#E5E2DD] placeholder:text-[#6B6B6B]'
    : 'border border-gray-300 bg-white text-[#1A1918] placeholder:text-gray-400';
  const smallBtnClass = bandOnly
    ? 'border border-[color:var(--km-rule)] bg-transparent text-[color:var(--km-ink-2)]'
    : darkMode
    ? 'border border-[#262626] bg-transparent text-muted-foreground hover:bg-[#1A1A1A]'
    : 'border border-gray-200 bg-transparent text-muted-foreground hover:bg-gray-50';

  // Status dot for avatar (green idle / amber thinking) — static, no animation
  const statusDotColor = isThinking ? 'bg-amber-500' : 'bg-green-500';
  const statusDotBorder = darkMode ? 'border-[#141414]' : 'border-gray-50';

  return (
    <div className={chatOnly ? 'w-full h-full flex flex-col min-h-0' : `w-full rounded-lg p-3 ${cardClass}`}
         data-testid="ai-chad-greeting">
      {/* =========================== TOP ROW =========================== */}
      <div className={chatOnly ? 'flex flex-col flex-1 min-h-0' : 'flex flex-col md:flex-row gap-4 items-stretch'}>
        {/* ============ LEFT: chat box ============ */}
        {!hideChat && (
        <div className={chatOnly
              ? 'flex-1 min-w-0 min-h-0 flex gap-2.5'
              : `flex-[2] min-w-0 rounded-md p-2.5 flex gap-2.5 min-h-[112px] ${innerChatBoxClass}`}
             data-testid="ai-chad-greeting-chat-box">
          {/* Avatar cell */}
          {!compact && (
          <div className="flex-shrink-0 flex flex-col items-center gap-1.5 pt-1">
            <div
              className="relative w-[72px] h-[72px] rounded-full overflow-hidden border-[1.5px] border-[#BC5D41]/40 shadow-sm"
              data-state={isThinking ? 'thinking' : 'idle'}
              data-testid="ai-chad-greeting-avatar"
            >
              <img
                src={aiChadAvatar}
                alt="AI Chad"
                className="w-full h-full object-cover object-top"
                draggable="false"
              />
              <span
                className={`absolute bottom-0.5 right-0.5 w-[14px] h-[14px] rounded-full border-2 ${statusDotColor} ${statusDotBorder}`}
                data-testid="ai-chad-greeting-status-dot"
                aria-hidden="true"
              />
            </div>
            <span className="text-[10px] uppercase tracking-wider text-[color:var(--km-rail-ink)]">AI Chad</span>
          </div>
          )}

          {/* Chat content cell */}
          <div className="km-chad-col flex-1 min-w-0 flex flex-col gap-2">
            {/* Transcript — plain text, no bubble styling */}
            <div
              ref={transcriptRef}
              role="log"
              aria-live="polite"
              className="flex-1 overflow-y-auto flex flex-col gap-2 max-h-[110px] md:max-h-[130px] pr-2"
              data-testid="ai-chad-greeting-transcript"
            >
              {/* First bubble = dynamic greeting (placeholder while loading).
                  Only shown when transcript is empty so prior history never gets
                  prefixed with a stale greeting on reload. While we're still
                  hydrating from /conversation, render nothing. */}
              {!compact && messages.length === 0 && hydrated && (
                <div className={`text-[13px] leading-relaxed self-start max-w-[92%] whitespace-pre-wrap break-words ${darkMode ? 'text-[#E5E2DD]' : 'text-foreground'}`}>
                  {renderChadMarkdown(dynamicGreeting || PLACEHOLDER_GREETING, stepLinkOpts)}
                </div>
              )}

              {compact && !showEarlier && messages.filter((m) => !m.hidden).length > 6 && (
                <button type="button" className="km-chad-btn self-start" onClick={() => setShowEarlier(true)} data-testid="ai-chad-earlier">earlier…</button>
              )}
              {messages.filter((m) => !m.hidden).filter((m, i, all) => !compact || showEarlier || i >= all.length - 6).map((m, idx, shown) => {
                // Older exchanges fade back so the current exchange pops;
                // hovering an older message restores full contrast.
                const isRecent = idx >= shown.length - 2;
                const fade = isRecent ? '' : 'opacity-50 hover:opacity-100 transition-opacity duration-200';
                // DP32 §5 — exactly ONE message is large at any time: the most
                // recent. When a newer reply arrives it drops to the older
                // treatment and the new one takes the large size.
                const km = idx === shown.length - 1 ? 'km-chad-msg--now' : 'km-chad-msg--past';
                return m.role === 'user' ? (
                  <div
                    key={m.id}
                    className={`${km} self-end max-w-[92%] text-right whitespace-pre-wrap break-words ${fade}`}
                  >
                    {m.content}
                  </div>
                ) : (
                  <React.Fragment key={m.id}>
                    <div className={`${km} self-start max-w-[92%] whitespace-pre-wrap break-words ${fade}`}>
                      {renderChadMarkdown(m.content, stepLinkOpts)}
                    </div>
                    {(m.proposed_actions || []).map((p) => (
                      <ActionCard key={p.id} proposal={p} onDone={noteActionResult} />
                    ))}
                  </React.Fragment>
                );
              })}

              {/* Stage 2: Activity Log proposal cards. Subtle border + tinted bg
                  to read as system suggestions distinct from regular messages. */}
              {logProposals.map((p) => (
                <div
                  key={p.id}
                  data-testid={`ai-chad-log-proposal-${p.id}`}
                  className={`self-start max-w-[92%] rounded-md border px-2.5 py-2 text-[12px] ${
                    darkMode
                      ? 'border-[#3A3A3A] bg-[#1F1F1F]/80'
                      : 'border-[#E5DFD3] bg-[#FAF6EE]'
                  }`}
                >
                  <div className={`font-semibold mb-0.5 ${darkMode ? 'text-[#E5E2DD]' : 'text-foreground'}`}>
                    {p.title}
                  </div>
                  {p.description && (
                    <div className={`mb-2 leading-snug ${darkMode ? 'text-[#A8A39A]' : 'text-muted-foreground'}`}>
                      {p.description}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid={`ai-chad-log-approve-${p.id}`}
                      onClick={() => approveProposal(p)}
                      className="km-chad-btn"
                    >
                      Save to Activity Log
                    </button>
                    <button
                      type="button"
                      data-testid={`ai-chad-log-dismiss-${p.id}`}
                      onClick={() => dismissProposal(p)}
                      className="km-chad-btn"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}

              {isThinking && (
                <div
                  className="self-start"
                  data-testid="ai-chad-greeting-typing"
                >
                  <span className="inline-flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#BC5D41] animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#BC5D41] animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#BC5D41] animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                </div>
              )}
            </div>

            {/* Input row — sticks to bottom of right cell */}
            <form onSubmit={handleSubmit} className="km-chad-compose flex gap-1.5 items-center">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask AI Chad anything…"
                aria-label="Message AI Chad"
                disabled={isThinking}
                ref={inputRef}
                className="km-chad-field flex-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-[#E0A03C]/40" 
                data-testid="ai-chad-greeting-input"
              />
              <button
                type="submit"
                aria-label="Send message"
                disabled={!input.trim() || isThinking}
                className="km-chad-send flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="ai-chad-greeting-send"
              >
                <Send className="w-3 h-3" />
                <span className="hidden sm:inline">Send</span>
              </button>
            </form>
          </div>
        </div>
        )}

        {/* ============ RIGHT: meta panel ============ */}
        {!chatOnly && (
        <div className={`flex-1 min-w-0 flex flex-col justify-between gap-2.5 border-t md:border-t-0 md:border-l ${dividerClass} pt-3 md:pt-0 md:pl-3.5 ${bandOnly ? 'md:max-w-[380px]' : ''}`}
             data-testid="ai-chad-greeting-meta">
          {/* Group 1: Clock + Weather */}
          <div>
            <div className="text-center">
              <p className={`text-[26px] lg:text-[26px] xl:text-[32px] font-extralight tracking-tighter leading-none m-0 ${bandInk}`}
                 data-testid="ai-chad-greeting-clock">
                {hourMinute}
              </p>
              <p className={`text-[11px] tracking-[1.5px] mt-1 m-0 ${bandInk2}`}>
                {ampm} · {weekdayLong} · {monthDay}
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 py-1.5" data-testid="ai-chad-greeting-weather">
              <span className="text-[22px]">{emoji || '☁️'}</span>
              <div>
                <p className="m-0 leading-none">
                  <span className="kmd-wx-temp">{temp != null ? `${temp}°F` : '--°F'}</span>{' '}
                  <span className="kmd-wx-cond">{conditions || ''}</span>
                </p>
                <p className="kmd-wx-hl m-0 mt-0.5">
                  High {high}° · Low {low}°
                </p>
              </div>
            </div>
          </div>

          {/* Group 2: Today / Events / Done */}
          <div className={`flex justify-center gap-[18px] py-1 border-t border-b ${dividerClass}`}
               data-testid="ai-chad-greeting-stats">
            <StatCell
              value={totalTasks}
              label="Today"
              valueClass={overdueCount > 0 ? bandLate : bandInk}
              labelClass={bandInk2}
            />
            <StatCell value={eventsToday} label="Events" valueClass={bandInk} labelClass={bandInk2} />
            {/* DP20: Done is a FIGURE, not a status. Green on sand is 1.08:1
                and cannot be rescued, so it takes the ink. */}
            <StatCell value={completedCount} label="Done"
                      valueClass={bandOnly ? bandInk : 'text-green-500'} labelClass={bandInk2} />
          </div>

          {/* Group 3: Customize + Health */}
          <div className="flex gap-1.5 justify-center">
            <button
              type="button"
              onClick={onCustomizeClick}
              className={`h-[22px] px-2 rounded-md text-[10px] inline-flex items-center gap-1 transition-colors ${smallBtnClass}`}
              data-testid="ai-chad-greeting-customize"
            >
              <Settings className="w-2.5 h-2.5" />
              <span>Customize</span>
            </button>
            <button
              type="button"
              className={`h-[22px] px-2 rounded-md text-[10px] inline-flex items-center gap-1 cursor-default ${smallBtnClass}`}
              data-testid="ai-chad-greeting-health"
              aria-label={`System status: ${healthMeta.label}`}
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${healthMeta.dot}`} />
              <span>{healthMeta.label}</span>
            </button>
          </div>
        </div>
        )}
      </div>

      {/* ====================== JOURNEY STRIP (project pages) ====================== */}
      {showJourneyStrip && (
        <div className={`mt-2.5 pt-2 border-t ${dividerClass}`} data-testid="ai-chad-journey-strip">
          <button
            type="button"
            onClick={toggleStrip}
            className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            aria-expanded={!stripCollapsed}
            data-testid="ai-chad-journey-strip-toggle"
          >
            <span className="inline-flex items-center gap-1.5">
              <Route className="w-3 h-3" />
              Project Journey
              {currentPhaseName && (
                <span data-testid="ai-chad-journey-strip-phase">· {currentPhaseName}</span>
              )}
              {journeyPct != null && (
                <span className="font-semibold" data-testid="ai-chad-journey-strip-progress">· {journeyPct}%</span>
              )}
            </span>
            {stripCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          {!stripCollapsed && (
            <div className="mt-2" data-testid="ai-chad-journey-strip-body">
              <JourneySteps
                project={journeyProject}
                phases={journeyPhases || journeyProject?.phases || []}
                onUpdate={onJourneyUpdate}
                darkMode={darkMode}
                teamMembers={journeyTeamMembers}
                enableKeyboardNav={false}
                compact
              />
            </div>
          )}
        </div>
      )}

      {/* =========================== FOOTER =========================== */}
      {!chatOnly && (
      <div className={`mt-2 pt-1.5 border-t ${dividerClass} flex gap-3.5 items-center`}
           data-testid="ai-chad-greeting-footer">
        {/* Quote — DP32 §3: rendered by MastheadBand at FULL BAND WIDTH. It is
            suppressed here when the band owns it, so there is only ever one. */}
        {!hideQuote && (
        <div className={`${tallies.length ? 'flex-[2]' : 'flex-1'} text-center kmd-mast-quote`}>
          <p className="m-0">
            <span className="kmd-mast-quote__text">&ldquo;{quoteText}&rdquo;</span>
            <span className="kmd-mast-quote__attrib">&mdash; {quoteAuthor}</span>
          </p>
        </div>
        )}
        {/* Tallies grouped — right 1/3, divider on left */}
        <div className={`flex-1 border-l ${dividerClass} pl-3.5 flex gap-3.5 justify-center flex-wrap`}>
          {tallies.map((t, i) => <Tally key={`t-${i}`} tally={t} />)}
        </div>
      </div>
      )}
    </div>
  );
}
